import React from 'react';
import { Box, Typography } from '@mui/material';
import { ColumnMappingEntry } from '../../../services/benchmarkService';
import { columnMethodLabel } from './suppliedSampleCopy';
import { dsVar, ink } from '../../../theme/tokens';

/**
 * How each supplied column was read: its name in the file, the field it was
 * taken as, and how that was decided.
 *
 * A column the model identified is marked, because a wrong mapping gives wrong
 * results with nothing else on the page to show it.
 */
const ColumnMappingTable: React.FC<{ entries: ColumnMappingEntry[] }> = ({ entries }) => {
    if (!entries.length) {
        return <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>No column mapping was recorded.</Typography>;
    }

    const head = { fontSize: '0.6875rem', color: dsVar('fg-muted'), fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.04em', pb: 0.75 };
    const mono = { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', wordBreak: 'break-word' as const };

    return (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
                <Box component="tr" sx={{ textAlign: 'left' }}>
                    <Box component="th" sx={head}>In your file</Box>
                    <Box component="th" sx={head}>Read as</Box>
                    <Box component="th" sx={head}>How</Box>
                </Box>
            </Box>
            <Box component="tbody">
                {entries.map((entry) => {
                    const byModel = entry.method === 'llm';
                    return (
                        <Box
                            component="tr"
                            key={entry.source}
                            sx={{ borderTop: `1px solid ${dsVar('line')}`, backgroundColor: byModel ? 'rgba(251, 191, 36, 0.06)' : 'transparent' }}
                        >
                            <Box component="td" sx={{ ...mono, color: dsVar('fg-soft'), py: 0.75, pr: 1 }}>{entry.source}</Box>
                            <Box component="td" sx={{ ...mono, color: dsVar('fg-2'), py: 0.75, pr: 1 }}>{entry.target}</Box>
                            <Box component="td" sx={{ fontSize: '0.75rem', py: 0.75, color: byModel ? ink('#FBBF24') : dsVar('fg-2'), fontWeight: byModel ? 600 : 400 }}>
                                {columnMethodLabel(entry.method)}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default ColumnMappingTable;
